/**
 * Onboarding's *Done* — w12b (C11, FR-A9 *"what is ready and the three next
 * things to do, each a link"*).
 *
 * A moment worth marking, so it is marked: a burst drawn in the theme's own
 * tokens (no image, no raw colour, nothing animated for a firm that asked
 * its system for less motion), then two honest lists — what is ready now, and
 * what is worth doing next.
 */

import { buttonClass } from '@fiveox/ui'
import { ArrowRight, Building2, Check, Globe, LayoutTemplate, PartyPopper, Scale } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

import type { FirmProfile } from '../../../lib/api/firm'
import { professionMeta, professionName } from './ProfessionCards'
import { nextStepsFor } from './steps'

function Burst() {
  // Twelve short rays and a scatter of dots around the icon. Static by
  // default; a gentle pop only where motion is welcome.
  const rays = Array.from({ length: 12 }, (_, i) => (i * 360) / 12)
  const dots = [
    [18, 30, 'fill-primary/40'],
    [150, 26, 'fill-primary/30'],
    [26, 108, 'fill-primary/25'],
    [146, 112, 'fill-primary/40'],
    [84, 8, 'fill-primary/30'],
    [8, 70, 'fill-primary/20'],
    [160, 70, 'fill-primary/20'],
  ] as const
  return (
    <svg
      viewBox="0 0 168 128"
      className="absolute inset-0 m-auto h-32 w-44 motion-safe:animate-[pulse_2.4s_ease-in-out_1]"
      aria-hidden
      focusable="false"
    >
      {rays.map((deg) => (
        <path
          key={deg}
          d="M84 22v-10"
          transform={`rotate(${deg} 84 64)`}
          className="stroke-primary/40"
          strokeWidth="3"
          strokeLinecap="round"
        />
      ))}
      {dots.map(([x, y, cls]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="3.5" className={cls} />
      ))}
      <rect x="36" y="18" width="7" height="7" rx="1.5" transform="rotate(20 39 21)" className="fill-primary/30" />
      <rect x="126" y="96" width="7" height="7" rx="1.5" transform="rotate(-25 129 99)" className="fill-primary/30" />
    </svg>
  )
}

function Ready({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          {title} <Check size={14} className="text-primary" aria-label="ready" />
        </span>
        <span className="block break-words text-sm text-muted-foreground">{children}</span>
      </span>
    </li>
  )
}

export function DoneStep({
  profile,
  templateName,
}: {
  profile: Partial<FirmProfile>
  templateName: string | null
}) {
  const name = profile.display_name || profile.legal_name || 'your practice'
  const profession = professionMeta(profile.profession)
  return (
    <div className="space-y-8" data-done="">
      <div className="relative flex flex-col items-center overflow-hidden rounded-3xl bg-primary/5 px-6 pb-8 pt-10 text-center">
        <div className="relative flex h-32 w-44 items-center justify-center">
          <Burst />
          <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg">
            <PartyPopper size={30} aria-hidden />
          </span>
        </div>
        <h2 className="mt-2 text-2xl font-semibold text-foreground sm:text-3xl">
          {name === 'your practice' ? 'Your practice is set up' : `${name} is set up`}
        </h2>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          Everything you answered is saved, and all of it can be changed in Settings whenever you
          like.
        </p>
      </div>

      <section aria-labelledby="ready-title" className="space-y-3">
        <h3 id="ready-title" className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Ready now
        </h3>
        <ul className="grid gap-4 sm:grid-cols-2">
          <Ready icon={<Scale size={16} aria-hidden />} title="Your profession">
            {profile.profession === 'other'
              ? `${professionName('other', profile.profession_label)} — no conduct policy applies; every website switch is yours.`
              : profession
                ? `${profession.plural} — conduct defaults from ${profession.institute}.`
                : 'Recorded.'}
          </Ready>
          <Ready icon={<Building2 size={16} aria-hidden />} title="Your firm">
            {profile.legal_name ?? 'Named'}
            {profile.email ? ` · ${profile.email}` : ''}
          </Ready>
          <Ready icon={<LayoutTemplate size={16} aria-hidden />} title="Your website">
            {templateName ?? profile.website_template ?? 'A template'} — drafted from your
            profession, not yet published.
          </Ready>
          <Ready icon={<Globe size={16} aria-hidden />} title="Your domain">
            {profile.custom_domain
              ? `${profile.custom_domain} — live once the platform verifies it.`
              : 'Not yet. Everything but your website and portal works without one.'}
          </Ready>
        </ul>
      </section>

      <section aria-labelledby="next-title" className="space-y-3">
        <h3 id="next-title" className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Three things to do next
        </h3>
        <ol className="grid gap-3 lg:grid-cols-3">
          {nextStepsFor(profile.profession).map((next, index) => (
            <li key={next.key}>
              <Link
                to={next.href}
                data-next={next.key}
                className="group flex h-full flex-col gap-2 rounded-2xl border border-border bg-card p-4 transition hover:border-primary hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {index + 1}
                </span>
                <span className="font-semibold text-foreground">{next.title}</span>
                <span className="text-sm text-muted-foreground">{next.body}</span>
                <span className="mt-auto inline-flex items-center gap-1 pt-1 text-sm font-medium text-primary">
                  Start <ArrowRight size={14} className="transition group-hover:translate-x-0.5" aria-hidden />
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Link to="/settings" className="text-sm font-medium text-muted-foreground hover:text-foreground">
          Review your settings
        </Link>
        <Link to="/" className={buttonClass({ variant: 'primary', size: 'lg' })}>
          Go to your Overview <ArrowRight size={16} aria-hidden />
        </Link>
      </div>
    </div>
  )
}
